/**
 * Botonera de la cabecera del formulario, con plegado.
 *
 * El defecto que resuelve: en un período de expensas ya emitido la cabecera
 * dibujaba OCHO botones —«Guardar», «Recalcular», «Emitir a los propietarios»,
 * «Imprimir liquidación», «Enviar por email», «Enviar a todas las unidades»,
 * «Archivar» y «Eliminar»— todos del mismo tamaño y del mismo peso visual. Como
 * la botonera y el título comparten la fila, medido a 1536 px el `<h1>` se
 * quedaba con 174 px para un nombre de 185: «Expensas 07/2…». El registro que
 * uno está mirando, cortado, para hacerle lugar a «Archivar».
 *
 * La respuesta es jerarquía, no achicar la letra: los primeros verbos quedan a
 * la vista y el resto se pliega en un menú. Tres reglas:
 *
 * 1. **El orden lo deciden los módulos, no esta pantalla.** Llegan ya ordenados
 *    por `sequence` desde la metadata; acá sólo se corta. Que «Emitir a los
 *    propietarios» esté a la vista y «Archivar» adentro del menú no es una lista
 *    de casos escrita en el frontend: es la secuencia que declaró el módulo.
 * 2. **Lo que se usa siempre NO se pliega.** «Guardar» y «Descartar» no entran
 *    acá: los dibuja el formulario, sueltos y antes de todo. Esconder el botón
 *    de guardar detrás de un menú sería cambiar un defecto chico por uno grave.
 * 3. **Plegar no es esconder.** Lo del menú se llega en un clic, con teclado y
 *    con lector de pantalla; y si sobra lugar —una cabecera con tres botones—
 *    no aparece ningún menú, porque no hay nada que plegar.
 *
 * Los botones NO se dibujan desde este archivo hacia abajo: cada barra
 * (acciones de servidor, reportes, correo) sigue siendo dueña de su lógica y de
 * sus diálogos, y sólo DESCRIBE sus botones con `ToolbarItem`. Así hay un único
 * menú «Más» para toda la cabecera en vez de uno por barra, que era la otra
 * forma de que esto quedara ilegible.
 */

import { useEffect, useRef, useState } from 'react';

import { IconChevronDown } from '@/components/shell/icons';
import { Button } from '@/components/shell/primitives';
import { ControlBloqueado, type SalidaDeBloqueo } from '@/views/MotivoBloqueo';

/**
 * Un botón de la cabecera, descrito sin dibujarlo.
 *
 * Es deliberadamente pobre: etiqueta, tono, si se puede apretar y qué hace. Lo
 * que necesita saber el que decide si va afuera o adentro del menú, y nada más.
 */
export interface ToolbarItem {
  key: string;
  label: string;
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost';
  disabled?: boolean;
  loading?: boolean;
  /** Ayuda del módulo (`help`). Va al `title`, que es para lo que sirve. */
  title?: string | null;
  /**
   * Por qué está apagado AHORA, en criollo.
   *
   * Cuando viene, el botón deja de ser un `<button disabled>` mudo y pasa a ser
   * un control bloqueado que **dice su motivo** al pasar el mouse, al enfocarlo
   * con teclado y al tocarlo. Ver `ControlBloqueado`. El `title` no alcanzaba:
   * un botón deshabilitado no recibe foco ni emite eventos.
   */
  reason?: string | null;
  /** Qué puede hacer la persona para destrabarlo, si hay algo. */
  salida?: SalidaDeBloqueo | null;
  onClick: () => void;
}

/** Cuántos botones quedan a la vista antes de que el resto se pliegue. */
const VISIBLES_POR_DEFECTO = 3;

// ---------------------------------------------------------------------------
// Menú «Más»
// ---------------------------------------------------------------------------
/**
 * Desplegable con lo que no entró en la fila.
 *
 * Se cierra al elegir, al apretar Escape —devolviendo el foco al botón, que es
 * lo que espera quien navega con teclado— y al hacer clic afuera. Las flechas
 * recorren las opciones. No usa ninguna librería: son cuarenta líneas y traerse
 * un paquete de menús para esto sería peor.
 */
function MoreMenu({ items, size }: { items: ToolbarItem[]; size: 'sm' | 'md' }) {
  const [abierto, setAbierto] = useState(false);
  const contenedor = useRef<HTMLDivElement | null>(null);
  const disparador = useRef<HTMLButtonElement | null>(null);
  const panel = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!abierto) return;

    const afuera = (evento: MouseEvent): void => {
      if (contenedor.current !== null && !contenedor.current.contains(evento.target as Node)) {
        setAbierto(false);
      }
    };
    const teclas = (evento: KeyboardEvent): void => {
      if (evento.key === 'Escape') {
        setAbierto(false);
        disparador.current?.focus();
      }
    };
    document.addEventListener('mousedown', afuera);
    document.addEventListener('keydown', teclas);
    // El foco entra al menú al abrirlo: sin esto, con teclado, «Más» abría algo
    // que quedaba a seis tabulaciones de distancia.
    //
    // Va al primero que se puede USAR, no al primero de la lista. Un ítem
    // bloqueado se dibuja con `aria-disabled` —para poder contar su motivo— así
    // que sigue siendo enfocable, y era el que se llevaba el foco: el globo se
    // abría solo apenas se abría «Más», encima de las opciones que sí
    // funcionaban, sin que nadie lo hubiera pedido. Con las flechas se llega
    // igual (ver `mover`, que sí los recorre).
    const opciones = panel.current?.querySelectorAll<HTMLButtonElement>('button:not([disabled])');
    const usables = [...(opciones ?? [])].filter(
      (boton) => boton.getAttribute('aria-disabled') === null,
    );
    (usables[0] ?? opciones?.[0])?.focus();
    return () => {
      document.removeEventListener('mousedown', afuera);
      document.removeEventListener('keydown', teclas);
    };
  }, [abierto]);

  /** Flechas arriba / abajo entre las opciones del panel. */
  const mover = (evento: React.KeyboardEvent<HTMLDivElement>): void => {
    if (evento.key !== 'ArrowDown' && evento.key !== 'ArrowUp') return;
    evento.preventDefault();
    const opciones = Array.from(
      panel.current?.querySelectorAll<HTMLButtonElement>('button:not([disabled])') ?? [],
    );
    if (opciones.length === 0) return;
    const actual = opciones.indexOf(document.activeElement as HTMLButtonElement);
    const paso = evento.key === 'ArrowDown' ? 1 : -1;
    const siguiente = (actual + paso + opciones.length) % opciones.length;
    opciones[siguiente]?.focus();
  };

  return (
    <div ref={contenedor} className="relative">
      <Button
        ref={disparador}
        size={size}
        variant="secondary"
        aria-haspopup="menu"
        aria-expanded={abierto}
        onClick={() => {
          setAbierto((previo) => !previo);
        }}
      >
        Más
        <IconChevronDown className="h-4 w-4" />
      </Button>

      {abierto && (
        <div
          ref={panel}
          role="menu"
          aria-label="Más acciones"
          onKeyDown={mover}
          // SIN `overflow-hidden`, y no es un detalle de estilo: el globo con el
          // motivo de un ítem bloqueado es `absolute` y sale del panel, así que
          // recortar acá lo cortaba por la mitad. Medido a 1280x900: el globo
          // mide 288x258 px y se veían 223x73 —se leía el arranque de la frase y
          // se perdían el resto, la aclaración y el botón «Guardar», que quedaba
          // fuera del área visible y por lo tanto no recibía el clic—. Es el
          // precedente del manual otra vez: el paso donde alguien solo se traba,
          // adentro de un «Más». El recorte sólo servía para redondear el fondo
          // de las opciones, que con `py-1` no llegan a los vértices igual.
          className="absolute right-0 z-40 mt-1 min-w-[14rem] max-w-xs rounded-lg border border-line bg-surface py-1 shadow-lg"
        >
          {items.map((item) => {
            const clases = [
              'flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors',
              'disabled:cursor-not-allowed disabled:opacity-55',
              item.variant === 'danger'
                ? 'text-danger hover:bg-danger-soft'
                : 'text-fg hover:bg-surface-raised',
            ].join(' ');

            // Un ítem del menú que está apagado con motivo se explica igual que
            // uno de la fila: es el MISMO botón, sólo que plegado. Que la
            // explicación dependiera de si la cabecera tenía lugar habría sido
            // el defecto del manual otra vez —«apretá Enviar a todas las
            // unidades», adentro de un «Más» que nadie nombraba—.
            const bloqueado =
              item.disabled === true &&
              item.loading !== true &&
              typeof item.reason === 'string' &&
              item.reason !== '';

            if (bloqueado) {
              return (
                <ControlBloqueado
                  key={item.key}
                  motivo={item.reason as string}
                  salida={item.salida ?? null}
                  className="w-full"
                  // Al costado y no abajo: adentro de un desplegable, un globo
                  // que baja se le monta encima a las opciones que sí se pueden
                  // usar. Ver `PosicionDelGlobo`.
                  posicion="al-costado"
                >
                  {(props) => (
                    <button
                      type="button"
                      role="menuitem"
                      aria-disabled
                      className={`${clases} cursor-help opacity-55`}
                      {...props}
                    >
                      {item.label}
                    </button>
                  )}
                </ControlBloqueado>
              );
            }

            return (
              <button
                key={item.key}
                type="button"
                role="menuitem"
                disabled={item.disabled === true || item.loading === true}
                {...(item.title === null || item.title === undefined ? {} : { title: item.title })}
                onClick={() => {
                  setAbierto(false);
                  item.onClick();
                }}
                className={clases}
              >
                {item.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Botonera
// ---------------------------------------------------------------------------
export interface ToolbarProps {
  /** Botones en orden de importancia: los primeros son los que quedan a la vista. */
  items: ToolbarItem[];
  /** Cuántos quedan afuera del menú. */
  visible?: number;
  size?: 'sm' | 'md';
  /**
   * Clave del botón que acaba de destrabarse y al que hay que devolverle el foco.
   *
   * Es la segunda mitad de «guardar y soltar»: el globo prometió que el botón
   * queda listo, y si el foco se queda donde estaba el botón que se desmontó,
   * quien navega con teclado tiene que salir a buscarlo a tientas. Se consume
   * una sola vez (`onEnfocado`).
   */
  focusKey?: string | null;
  onEnfocado?: () => void;
}

export function Toolbar({
  items,
  visible = VISIBLES_POR_DEFECTO,
  size = 'md',
  focusKey = null,
  onEnfocado,
}: ToolbarProps) {
  const contenedor = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (focusKey === null) return;
    // Se recorre y se compara el atributo en vez de armar un selector: la clave
    // la escribe cada barra (`accion-<nombre>`, `mail-masivo-<nombre>`) y meter
    // texto ajeno adentro de un selector es la clase de detalle que un día
    // explota con un nombre raro.
    const nodo = [...(contenedor.current?.querySelectorAll<HTMLButtonElement>('[data-toolbar-key]') ?? [])].find(
      (candidato) => candidato.getAttribute('data-toolbar-key') === focusKey,
    );
    // El botón ya no existe: no hay a qué volver y el pedido se descarta.
    if (nodo === undefined) {
      onEnfocado?.();
      return;
    }
    // Sigue bloqueado: el guardado todavía está en vuelo. Se espera sin
    // consumir el pedido — este efecto vuelve a correr en cada render del
    // formulario, así que en cuanto el botón se destrabe el foco llega. Sin
    // esta espera, el pedido se consumía en el primer render posterior al
    // clic (con el guardado a mitad de camino) y el foco no llegaba nunca:
    // el globo prometía que el botón quedaba listo y quien navega con teclado
    // se quedaba en la nada. Si el guardado falla, `FormView` limpia el pedido
    // desde su `onError`, así que esto no se queda esperando para siempre.
    if (nodo.getAttribute('aria-disabled') !== null) return;

    nodo.focus();
    onEnfocado?.();
    // SIN lista de dependencias, a propósito: lo que hay que vigilar no es un
    // valor de React sino el estado del DOM —si el botón ya se destrabó—, y eso
    // cambia en un render en el que `focusKey` sigue siendo el mismo string. Con
    // `[focusKey, onEnfocado]` el efecto no volvía a correr cuando el guardado
    // terminaba y el foco no llegaba nunca. El cuerpo corta en la primera línea
    // mientras no haya pedido pendiente, así que correr en cada render no cuesta
    // nada.
  });

  if (items.length === 0) return null;

  // Con uno solo de sobra no se pliega nada: un menú «Más» con una sola opción
  // adentro es un clic de más para esconder un botón que entraba igual.
  const corte = items.length <= visible + 1 ? items.length : visible;
  const alaVista = items.slice(0, corte);
  const enElMenu = items.slice(corte);

  return (
    // `contents`: el envoltorio existe sólo para poder buscar adentro el botón
    // que hay que enfocar; no puede aportar una caja de flex propia o la
    // cabecera dejaría de plegar como pliega hoy.
    <span ref={contenedor} className="contents">
      {alaVista.map((item) => {
        // Apagado CON motivo: el botón se dibuja bloqueado y cuenta por qué.
        // Apagado SIN motivo no debería existir más en el sistema; si aparece,
        // se comporta como antes en vez de romper la pantalla.
        const bloqueado =
          item.disabled === true &&
          item.loading !== true &&
          typeof item.reason === 'string' &&
          item.reason !== '';

        if (bloqueado) {
          return (
            <ControlBloqueado key={item.key} motivo={item.reason as string} salida={item.salida ?? null}>
              {(props) => (
                <Button
                  blocked
                  size={size}
                  variant={item.variant ?? 'secondary'}
                  data-toolbar-key={item.key}
                  {...props}
                >
                  {item.label}
                </Button>
              )}
            </ControlBloqueado>
          );
        }

        return (
          <Button
            key={item.key}
            size={size}
            variant={item.variant ?? 'secondary'}
            disabled={item.disabled === true}
            loading={item.loading === true}
            data-toolbar-key={item.key}
            {...(item.title === null || item.title === undefined ? {} : { title: item.title })}
            onClick={item.onClick}
          >
            {item.label}
          </Button>
        );
      })}
      {enElMenu.length > 0 && <MoreMenu items={enElMenu} size={size} />}
    </span>
  );
}
